import React from 'react';
import Modal from '@material-ui/core/Modal';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Flatpickr from "react-flatpickr";
import ParticipantIdentity from '../shared/ParticipantIdentity';

// MUI transitions
import Fade from '@material-ui/core/Fade';
import Backdrop from '@material-ui/core/Backdrop';
import Select2Wrapper from '../Select2Wrapper';
import { formatViewDate } from '../../helpers/dateHelper';

const DATETIME_DISPLAY_FORMAT = "Y-m-d H:i";

export default class EhcParticipantConfirmation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      serviceOrder: { ...props.serviceOrder },
      participant: { ...props.participant },
      doctorOptions: [],
      doctorsHospitalId: null,
      submitting: false,
      error: null
    };
  }

  baseUrl = (company_id) => `/api/ehc/companies/${company_id}/participants`;

  labels = () => this.props.labels?.confirm || {};

  handleChange = (field, value) => {
    const newParticipantData = { ...this.state.participant, [field]: value };
    this.setState({ participant: newParticipantData });
  }

  parseError(result) {
    if (result.errors && typeof result.errors === 'object' && !Array.isArray(result.errors)) {
      return Object.entries(result.errors).map(([k, v]) => `${k}: ${v}`).join(', ');
    }
    if (typeof result.errors === 'string') return result.errors;
    if (Array.isArray(result.errors) && result.errors.length > 0) return result.errors.join(', ');
    if (result.message) return result.message;
    return this.labels().error_generic;
  }

  // PE-435: the old two-stage "Confirm" -> "Are you sure? Yes/No" prompt is gone. The dialog
  // now has a real header and states its consequence up front, and the old stage-two reset was
  // wired to a click handler wrapping the whole body, so touching any field silently cancelled it.
  handleParticipantConfirm = async (event) => {
    event.preventDefault();

    const participant = this.state.participant;
    if (this.state.submitting) return;

    let confirmedDatetime = participant.confirmed_datetime;
    if (!confirmedDatetime && participant.visit_date) {
      const defaultToConfirmTime = '10:00:00';
      confirmedDatetime = this.state.participant.visit_date + ' ' + defaultToConfirmTime;
    }

    if (!this.props.editable || !participant ||
      !participant.doctor_ids || participant.doctor_ids.length < 1 ||
      !confirmedDatetime
    ) return;

    const formData = {
      doctor_ids: participant.doctor_ids,
      confirmed_datetime: confirmedDatetime,
      notes: participant.notes
    };

    const csrfToken = document.querySelector('[name=csrf-token]').content;

    this.setState({ submitting: true });

    try {
      const response = await fetch(this.baseUrl(participant.company_id) + '/' + participant.id + '/confirm', {
        method: 'POST',
        body: JSON.stringify(formData),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          'X-CSRF-Token': csrfToken
        }
      });

      const result = await response.json();

      if (!response.ok) {
        this.setState({ error: this.parseError(result), submitting: false });
        return;
      }

      this.setState({ error: null, submitting: false });
      this.props.onClose();
      if (this.props.tableRef.current) {
        this.props.tableRef.current.onQueryChange();
      }
    } catch (err) {
      this.setState({ error: this.labels().error_generic, submitting: false });
    }
  };

  fetchDoctors = async (hospitalId) => {
    try {
      const response = await fetch(`/api/doctors${hospitalId ? '?hospital_id=' + hospitalId : ''}`);
      if (response.status >= 400) return [];
      return await response.json();
    } catch (error) {
      console.error('Fetch doctors error:', error);
      return [];
    }
  };

  loadDoctorsIfNeeded = async (hospitalId) => {
    if (this.state.doctorsHospitalId === hospitalId) return;

    if (!hospitalId) {
      this.setState({ doctorOptions: [], doctorsHospitalId: hospitalId });
      return;
    }

    const doctors = await this.fetchDoctors(hospitalId);
    const doctorOptions = doctors?.map(doctor => ({ value: doctor.id, label: doctor.full_name })) || [];
    this.setState({ doctorOptions, doctorsHospitalId: hospitalId });
  };

  renderLockedField = (id, label, value) => (
    <div className="col-md-6">
      <div className="form-group">
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          type="text"
          disabled
          readOnly
          className="form-control form-modal__locked-field"
          value={value}
        />
        <i className="form-group__bar"></i>
      </div>
    </div>
  );

  // =====================
  // Render Component
  // =====================
  async componentDidMount() {
    await this.loadDoctorsIfNeeded(this.state.participant?.hospital_id);
  }

  async componentDidUpdate(prevProps) {
    await this.loadDoctorsIfNeeded(this.state.participant?.hospital_id);

    // If participant prop changes
    if (this.props.participant !== prevProps.participant) {
      this.setState({ participant: { ...this.props.participant }, error: null, submitting: false });
    }
  }

  render() {
    const labels = this.labels();
    const defaultToConfirmTime = '10:00:00';
    const participant = this.state.participant;
    const batchPeriod = `${formatViewDate(this.state.serviceOrder.start_date)} - ${formatViewDate(this.state.serviceOrder.end_date)}`;

    return (
      <Modal
        open={this.props.open}
        onClose={this.props.onClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
        style={{ overflow: 'auto' }}
      >
        <Fade in={this.props.open} timeout={500}>
          <Box className="form-modal">
            <form onSubmit={this.handleParticipantConfirm}>
              <div className="form-modal__head">
                <div>
                  <Typography variant="h6" className="mb-0">{labels.title}</Typography>
                  <small className="text-muted">{labels.subtitle}</small>
                </div>
                <IconButton size="small" aria-label={labels.close} onClick={this.props.onClose}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>

              <div className="form-modal__body">
                <ParticipantIdentity participant={participant} labels={labels} />

                {participant &&
                  <div className="row">
                    {this.renderLockedField('service_date_range', labels.batch_period, batchPeriod)}
                    {this.renderLockedField(
                      'requested_service_date',
                      labels.requested_service_date,
                      formatViewDate(participant.visit_date || '') || '-'
                    )}

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="doctor_ids">{labels.doctors} *</label>
                        <Select2Wrapper
                          required
                          className="form-control"
                          value={participant.doctor_ids?.map(doctorId => doctorId.toString()) || []}
                          name="doctor_ids"
                          onChange={(e) => this.handleChange("doctor_ids", e.target.selectedOptions ? Array.from(e.target.selectedOptions, option => +option.value) : [])}
                          useDirectParent={true}
                          options={{
                            multiple: true,
                            disabled: !this.props.editable,
                            allowClear: true,
                            closeOnSelect: false,
                            placeholder: labels.search_doctors,
                          }}
                          items={this.state.doctorOptions}
                        >
                        </Select2Wrapper>
                        <i className="form-group__bar"></i>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="confirmed_datetime">{labels.confirmed_datetime} *</label>
                        <Flatpickr
                          required
                          placeholder="YYYY-MM-DD HH:mm"
                          disabled={!this.props.editable}
                          className="form-control"
                          title={labels.confirmed_datetime_hint}
                          value={participant.confirmed_datetime || (participant.visit_date ? participant.visit_date + ' ' + defaultToConfirmTime : '')}
                          onClose={([selectedDate]) => {
                            if (selectedDate) this.handleChange("confirmed_datetime", selectedDate.toISOString());
                          }}
                          options={{
                            minDate: "today",
                            defaultHour: 0,
                            dateFormat: DATETIME_DISPLAY_FORMAT,
                            enableTime: true,
                            time_24hr: true,
                            minuteIncrement: 1,
                            allowInput: true
                          }}
                        />
                        <i className="form-group__bar"></i>
                        <small className="text-muted">{labels.confirmed_datetime_hint}</small>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="notes">{labels.notes}</label>
                        <textarea
                          id="notes"
                          placeholder={labels.notes_placeholder}
                          disabled={!this.props.editable}
                          className="form-control form-modal__notes"
                          value={participant.notes || ''}
                          name="notes"
                          onChange={(e) => this.handleChange("notes", e.target.value)}
                        />
                        <i className="form-group__bar"></i>
                      </div>
                    </div>
                  </div>
                }

                <p className="form-modal__info">{labels.consequence}</p>

                {this.state.error &&
                  <div className="alert alert-danger" role="alert">{this.state.error}</div>
                }
              </div>

              <div className="form-modal__foot">
                <Button variant="outlined" onClick={this.props.onClose}>
                  {labels.cancel}
                </Button>
                {this.props.editable &&
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!participant || this.state.submitting}
                    className="ml-2"
                    type="submit"
                  >
                    {labels.submit}
                  </Button>
                }
              </div>
            </form>
          </Box>
        </Fade>
      </Modal>
    );
  }
}
